import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingDown, History, StickyNote } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { NotesPanel } from "./Notes";
import { api } from "../lib/api";
import { money, monthLabel } from "../lib/format";
import { Panel, Empty } from "./Widgets";

export function OwesBadge({ amount, testId }) {
  if (!(amount < 0)) return null;
  return (
    <span data-testid={testId} title="Shortfall carried to next month"
      className="ml-2 inline-block bg-rose-500/15 text-rose-300 border border-rose-500/40 font-mono text-[10px] uppercase px-2 py-0.5 rounded">
      owes {money(-amount)}
    </span>
  );
}

export function NegativeMerchants({ month }) {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/negative-merchants", { params: { month } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Panel title="Negative merchants" className="mb-6" action={<TrendingDown size={16} className="text-rose-400" />}>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131B2E] border-slate-800 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="negative-notes-dialog">
          <DialogHeader><DialogTitle>{sel?.merchant_name || sel?.merchant_id} · follow-up</DialogTitle></DialogHeader>
          {sel && <NotesPanel targetType="merchant" targetId={sel.merchant_id} onChange={load} />}
        </DialogContent>
      </Dialog>
      {!rows ? <Empty>Loading…</Empty> : rows.length === 0 ? <Empty>No negative residuals in {monthLabel(month)}.</Empty> : (
        <table className="tbl w-full" data-testid="negative-merchants-table">
          <thead><tr><th>Merchant</th><th>Belongs to</th><th className="!text-right">Residual</th><th className="!text-right">Taken from agent</th><th className="!text-right">Follow-up</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={`${r.merchant_id}-${r.owner}`} data-testid={`negative-merchant-${r.merchant_id}`}>
              <td>{r.merchant_name || "—"}
                {r.streak >= 2 && <span data-testid={`repeat-negative-${r.merchant_id}`} title="Negative in consecutive months"
                  className="ml-2 inline-block bg-amber-500/15 text-amber-300 border border-amber-500/40 font-mono text-[10px] uppercase px-2 py-0.5 rounded">repeat · {r.streak} mo</span>}
                <div className="font-mono text-[11px] text-slate-500">{r.merchant_id}</div></td>
              <td className="text-slate-300">{r.agent_id ? <Link to={`/admin/agents/${r.agent_id}`} className="hover:text-blue-300">{r.owner}</Link> : r.owner}</td>
              <td className="text-right font-mono text-rose-400">{money(r.residual)}</td>
              <td className="text-right font-mono text-rose-300">{r.agent_id ? money(r.deducted) : "—"}</td>
              <td className="text-right">
                <button data-testid={`negative-notes-${r.merchant_id}`} onClick={() => { setSel(r); setOpen(true); }}
                  className={`inline-flex items-center gap-1 text-[11px] font-mono uppercase px-2 py-1 rounded border transition-colors ${r.open_notes ? "border-blue-500/40 text-blue-300 hover:bg-blue-500/10" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}>
                  <StickyNote size={12} />{r.open_notes ? `${r.open_notes} open` : "Add note"}
                </button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </Panel>
  );
}

export function CarryHistory({ agentId, showAgent, hideEmpty, className = "" }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/carry-history", { params: agentId ? { agent_id: agentId } : {} }).then((r) => setRows(r.data)); }, [agentId]);
  if (hideEmpty && rows && rows.length === 0) return null;
  return (
    <Panel title="Carried balances" className={className} action={<History size={16} className="text-slate-400" />}>
      {!rows ? <Empty>Loading…</Empty> : rows.length === 0 ? <Empty>No amounts have been carried forward.</Empty> : (
        <div className="overflow-x-auto">
          <table className="tbl w-full" data-testid="carry-history-table">
            <thead><tr><th>Month</th>{showAgent && <th>Agent</th>}<th className="!text-right">Earned</th><th className="!text-right">Carried in</th>
              <th className="!text-right">Recovered</th><th className="!text-right">Paid</th><th className="!text-right">Carried to next month</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={`${r.agent_id}-${r.month}`} data-testid={`carry-row-${r.agent_id}-${r.month}`}>
                <td className="font-mono">{monthLabel(r.month)}</td>
                {showAgent && <td><Link to={`/admin/agents/${r.agent_id}`} className="hover:text-blue-300">{r.agent_name}</Link></td>}
                <td className={`text-right font-mono ${r.earned < 0 ? "text-rose-400" : ""}`}>{money(r.earned)}</td>
                <td className="text-right font-mono text-rose-300">{money(r.carry_in)}</td>
                <td className="text-right font-mono text-emerald-400">{money(r.recovered)}</td>
                <td className="text-right font-mono">{money(r.payout)}</td>
                <td className="text-right font-mono text-rose-400">{money(r.carry_out)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
